import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document } from 'flexsearch';
import { Link } from 'react-router-dom';
import matter from 'gray-matter';
import { FileText, Search } from 'lucide-react';
import { categoryOrder, searchableDocs } from './docsRegistry';

interface Doc {
  id: number;
  slug: string;
  title: string;
  subtitle?: string;
  category: string;
  content: string;
  [key: string]: any;
}

interface GroupedResults {
  [category: string]: Doc[];
}

const DocsSearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [groupedResults, setGroupedResults] = useState<GroupedResults>({});
  const [totalResults, setTotalResults] = useState(0);
  const [index, setIndex] = useState<Document<Doc> | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchDocs = async () => {
      const allDocs = await Promise.all(
        searchableDocs.map(async (doc, i) => {
          const res = await fetch(`/docs/${doc.filename}.md`);
          if (!res.ok) throw new Error(`Docs article not found: ${doc.filename}`);

          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('text/html')) {
            throw new Error(`Docs article resolved to HTML: ${doc.filename}`);
          }

          const text = await res.text();
          const trimmedText = text.trim().toLowerCase();
          if (trimmedText.startsWith('<!doctype html') || trimmedText.startsWith('<html')) {
            throw new Error(`Docs article resolved to HTML: ${doc.filename}`);
          }

          const { data, content } = matter(text);
          return {
            id: i,
            slug: data.slug || doc.slug,
            title: data.title || doc.title,
            subtitle: data.subtitle || doc.subtitle || '',
            category: doc.category || data.category,
            content: content,
          };
        })
      );

      const newIndex = new Document<Doc>({
        document: {
          id: 'id',
          index: ['title', 'content', 'subtitle'],
          store: ['slug', 'title', 'subtitle', 'content', 'category'],
        },
        tokenize: 'forward',
      });

      allDocs.forEach(doc => {
        newIndex.add(doc);
      });

      setIndex(newIndex);
    };

    fetchDocs();
  }, []);

  useEffect(() => {
    if (!query || !index) {
      setGroupedResults({});
      setTotalResults(0);
      return;
    }

    const searchResults = index.search<true>(query, { enrich: true, limit: 15 });

    const uniqueResults = new Map<string, Doc>();
    searchResults.forEach(resultSet => {
      resultSet.result.forEach(item => {
        if (item.doc) {
          uniqueResults.set(item.doc.slug, item.doc);
        }
      });
    });
    
    const resultsArray = Array.from(uniqueResults.values());
    
    const grouped = resultsArray.reduce((acc, doc) => {
      const category = doc.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(doc);
      return acc;
    }, {} as GroupedResults);

    setGroupedResults(grouped);
    setTotalResults(resultsArray.length);

  }, [query, index]);

  const highlightMatch = useCallback((text: string) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
    const normalizedQuery = query.toLowerCase();
    return (
      <>
        {text.split(regex).map((part, i) =>
          part.toLowerCase() === normalizedQuery ? (
            <mark key={i} className="docs-search-mark">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  }, [query]);

  const getSnippet = useCallback((text: string) => {
    if (!query || !text) {
        return text.substring(0, 120) + (text.length > 120 ? '...' : '');
    }
    const matchIndex = text.toLowerCase().indexOf(query.toLowerCase());

    if (matchIndex === -1) {
        return text.substring(0, 120) + (text.length > 120 ? '...' : '');
    }

    const start = Math.max(0, matchIndex - 40);
    const end = Math.min(text.length, matchIndex + query.length + 80);

    const prefix = start > 0 ? '...' : '';
    const suffix = end < text.length ? '...' : '';

    return (
      <>
        {prefix}
        {highlightMatch(text.substring(start, end))}
        {suffix}
      </>
    )
  }, [query, highlightMatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setQuery('');
        setGroupedResults({});
        setTotalResults(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClose = () => {
    setQuery('');
    setGroupedResults({});
    setTotalResults(0);
  };

  return (
    <div className="docs-search" ref={searchContainerRef}>
      <div className="docs-search-control">
        <Search className="docs-search-icon" aria-hidden="true" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles..."
          aria-label="Search articles"
          className="docs-search-input"
        />
        <span className="docs-search-key" aria-hidden="true">
          /
        </span>
      </div>
      {totalResults > 0 && (
        <div className="docs-search-results">
          <div className="docs-search-results-inner">
            {Object.entries(groupedResults)
              .sort(([categoryA], [categoryB]) => {
                const indexA = categoryOrder.indexOf(categoryA);
                const indexB = categoryOrder.indexOf(categoryB);
                if (indexA === -1) return 1; // Puts categories not in the list at the end
                if (indexB === -1) return -1;
                return indexA - indexB;
              })
              .map(([category, docs]) => (
                <div key={category} className="docs-search-result-group">
                  <h3 className="docs-search-result-heading">
                    {category}
                  </h3>
                  <ul className="docs-search-result-list">
                    {docs.map((doc) => (
                      <li key={doc.slug}>
                        <Link
                          to={`/docs/${doc.slug}`}
                          className="docs-search-result"
                          onClick={handleClose}
                        >
                          <div className="docs-search-result-layout">
                            <FileText className="docs-search-result-icon" aria-hidden="true" />
                            <div className="docs-search-result-copy">
                              <h4>
                                {highlightMatch(doc.title)}
                              </h4>
                              {doc.subtitle && (
                                <p className="docs-search-result-subtitle">
                                  {highlightMatch(doc.subtitle)}
                                </p>
                              )}
                              <p className="docs-search-result-snippet">
                                {getSnippet(doc.content)}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocsSearchBar; 
